import * as React from 'react';
import { styled, concatStyleSetsWithProps, ContextualMenu, DefaultButton, DirectionalHint, Icon, TooltipHost, mergeStyles, Spinner, SpinnerSize, setFocusVisibility } from '@fluentui/react';
import { getSensitivityButtonDefaultStyle, sensitivitySpinnerStyle } from './SensitivityButton.styles';
import { sensitivityButtonDividerId, sensitivityButtonId, sensitivityButtonLabelId, sensitivityLabelTooltipId } from './utilities/testConstants';
import { SensitivityIcon, SensitivityIconType } from './SensitivityIcon';
import { useInformationProtection } from './hooks/useInformationProtection';
import { ErrorEvent } from './telemetry/ErrorEvents';
import { StylingProviderEvents } from '@fluidx/office-fluid-types';
import { ShowJustificationDialog } from './SensitivityJustificationDialog';
import { getSensitivityContextMenuItems } from './utilities/getSensitivityContextMenuItems';
import { sendUserActionEvent, sendErrorEvent } from '@fluidx/telemetry';
import { defaultLocale, getUIDirection, getStringTableWithFallback, isMobile } from '@fluidx/utilities';
import { getStringTable } from './localizationUtilities';
import { getComponentsIcon } from '@fluidx/icons/lib/useIconNames/components';
import { UserAction } from './telemetry/UserAction';
import { isGetLabelEnabled, isSetLabelEnabled, mipPhase2SettingName } from './utilities/informationProtectionSettingsUtilities';
import { getLabelById } from './utilities/getLabelById';
import { ShowSensitivityLabelErrorDialog } from './sensitivityLabelErrorDialog';
export const SensitivityButton = /*#__PURE__*/ React.memo(styled(function SensitivityButton(props) {
    const { informationProtectionService , settingsProvider , resolvedUrl , isCompact , styles , theme , logger , hideDivider , customTokens , stylingProvider  } = props;
    const [sensitivityContextMenu, setSensitivityContextMenu] = React.useState([]);
    const [showContextualMenu, setShowContextualMenu] = React.useState(false);
    const [showSpinner, setShowSpinner] = React.useState(false);
    const [stylingProviderThemeProperties, setStylingProviderThemeProperties] = React.useState(readThemingProperties(stylingProvider?.themeProperties));
    const [currentDocument, setCurrentDocument] = React.useState();
    const locale = props.locale || defaultLocale;
    const [informationProtectionStringTable, setStringTable] = React.useState(undefined);
    const isMobileDevice = isMobile();
    React.useEffect(()=>{
        getStringTableWithFallback(getStringTable, locale).then((table)=>{
            setStringTable(table);
        }).catch(()=>{});
    }, [
        locale
    ]);
    React.useEffect(()=>{
        if (!stylingProvider) {
            return;
        }
        const onStylingChanged = async ()=>{
            setStylingProviderThemeProperties(readThemingProperties(stylingProvider.themeProperties));
        };
        stylingProvider.on(StylingProviderEvents.themePropertiesChanged, onStylingChanged);
        return ()=>{
            stylingProvider.off(StylingProviderEvents.themePropertiesChanged, onStylingChanged);
        };
    }, [
        stylingProvider
    ]);
    const buttonRef = React.useRef(null);
    const onRefChange = React.useCallback((element)=>{
        if (element) {
            setCurrentDocument(element.ownerDocument);
        }
    }, []);
    const { appliedSensitivityLabel , sensitivityLabels , currentLabel , parentLabel , isDowngradeJustificationRequired , moreInfoUrl , supportsCLP , canWrite  } = useInformationProtection(informationProtectionService, resolvedUrl);
    const resolvedStyles = React.useMemo(()=>{
        return concatStyleSetsWithProps({
            theme,
            customTokens,
            contextualMenuShown: showContextualMenu
        }, styles);
    }, [
        theme,
        styles,
        customTokens,
        showContextualMenu
    ]);
    const toggleContextualMenu = React.useCallback(()=>{
        if (isSetLabelEnabled(settingsProvider)) {
            logger && sendUserActionEvent(logger, {
                eventName: UserAction.ShowOrHideLabelPicker,
                showMenu: !showContextualMenu
            });
            setShowContextualMenu(!showContextualMenu);
        } else {
            logger && sendUserActionEvent(logger, {
                eventName: UserAction.ShowOrHideLabelPicker,
                showMenu: false,
                settingName: mipPhase2SettingName,
                settingValue: isSetLabelEnabled(settingsProvider),
                settingsProviderExists: settingsProvider !== undefined
            });
        }
    }, [
        showContextualMenu,
        logger,
        settingsProvider
    ]);
    const dismissContextualMenu = React.useCallback((ev)=>{
        if (showContextualMenu) {
            toggleContextualMenu();
        }
        if (ev && 'key' in ev && ev.key === 'Escape') {
            buttonRef.current?.focus();
            setFocusVisibility(true);
        }
    }, [
        showContextualMenu,
        toggleContextualMenu
    ]);
    const onApplyLabel = React.useCallback(/* eslint-disable-next-line @typescript-eslint/no-explicit-any
-- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */ (_ev, item)=>{
        if (item && sensitivityLabels && sensitivityLabels.length > 0) {
            const sensitivityPayload = {
                id: item.key,
                assignmentMethod: 'privileged',
                actionSource: 'manual',
                ifMatchLabelId: appliedSensitivityLabel?.sensitivityLabelId
            };
            const newLabel = getLabelById(sensitivityLabels, item.key);
            const applyOrRemoveLabel = async (justification)=>{
                sensitivityPayload.justificationText = justification;
                // Start showing the spinner when we apply or remove the label
                // This spinner state will be reset when we receive the new label
                // information from InformationProtectionService through
                // 'sensitivityLabelChanged' event or if the set or remove current
                // label APIs fail.
                setShowSpinner(true);
                let result = false;
                if (item.isChecked) {
                    logger && sendUserActionEvent(logger, {
                        eventName: UserAction.RemoveLabel,
                        isJustified: !!justification
                    });
                    if (await informationProtectionService.getIsMandatoryLabeling(resolvedUrl)) {
                        if (informationProtectionStringTable) {
                            ShowSensitivityLabelErrorDialog(informationProtectionStringTable, 'mandatory', resolvedStyles.justification, stylingProviderThemeProperties, currentDocument);
                        } else {
                            logger && sendErrorEvent(logger, {
                                eventName: ErrorEvent.StringTableLoadFailure
                            });
                        }
                    } else {
                        result = await informationProtectionService.removeCurrentLabel(resolvedUrl, sensitivityPayload);
                    }
                } else {
                    logger && sendUserActionEvent(logger, {
                        eventName: UserAction.ApplyLabel,
                        isJustified: !!justification
                    });
                    result = await informationProtectionService.setCurrentLabel(resolvedUrl, sensitivityPayload);
                }
                if (!result) {
                    setShowSpinner(false);
                }
            };
            // Show justification dialog if downgrade justification is required.
            if (isDowngradeJustificationRequired && informationProtectionStringTable && currentLabel && newLabel && newLabel.sensitivity <= currentLabel.sensitivity) {
                ShowJustificationDialog(informationProtectionStringTable, applyOrRemoveLabel, moreInfoUrl, resolvedStyles.justification, stylingProviderThemeProperties, currentDocument);
            } else {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                applyOrRemoveLabel();
            }
        }
    }, [
        sensitivityLabels,
        appliedSensitivityLabel?.sensitivityLabelId,
        isDowngradeJustificationRequired,
        informationProtectionStringTable,
        currentLabel,
        informationProtectionService,
        resolvedStyles.justification,
        stylingProviderThemeProperties,
        moreInfoUrl,
        logger,
        resolvedUrl,
        currentDocument
    ]);
    const localeDirection = props.localeDirection || getUIDirection(locale);
    const chevronIcon = getComponentsIcon(localeDirection === 'rtl' ? 'FFXCChevronLeft' : 'FFXCChevronRight');
    const checkMarkIcon = getComponentsIcon('FFXCCheckMark');
    const sensitivityMenuItemAs = React.useCallback(({ item  })=>{
        return /*#__PURE__*/ React.createElement("div", {
            className: mergeStyles(resolvedStyles.contextualMenu?.contextualMenuItemWrapper)
        }, /*#__PURE__*/ React.createElement("div", {
            className: mergeStyles(resolvedStyles.contextualMenu?.contextualMenuItemIconWrapper)
        }, !(item.subMenuProps || item.hasIcon === false) && /*#__PURE__*/ React.createElement(SensitivityIcon, {
            type: item.sensitivityIconType
        })), /*#__PURE__*/ React.createElement("div", {
            className: mergeStyles(resolvedStyles.contextualMenu?.contextualMenuItemLabelWrapper)
        }, /*#__PURE__*/ React.createElement("div", {
            className: mergeStyles(resolvedStyles.contextualMenu?.contextualMenuItemLabel)
        }, item.text), item.subMenuProps && /*#__PURE__*/ React.createElement(Icon, {
            iconName: chevronIcon
        }), item.isChecked && /*#__PURE__*/ React.createElement(Icon, {
            iconName: checkMarkIcon
        })));
    }, [
        checkMarkIcon,
        chevronIcon,
        resolvedStyles.contextualMenu
    ]);
    React.useEffect(()=>{
        const showContextualMenu = ()=>{
            setShowContextualMenu(true);
        };
        const stopSpinner = ()=>{
            setShowSpinner(false);
        };
        informationProtectionService.on('openLabelPicker', showContextualMenu);
        informationProtectionService.on('sensitivityLabelChanged', stopSpinner);
        return ()=>{
            informationProtectionService.off('openLabelPicker', showContextualMenu);
            informationProtectionService.off('sensitivityLabelChanged', stopSpinner);
        };
    }, [
        informationProtectionService
    ]);
    React.useEffect(()=>{
        if (sensitivityLabels) {
            const contextMenu = getSensitivityContextMenuItems(appliedSensitivityLabel, sensitivityLabels, onApplyLabel, sensitivityMenuItemAs, resolvedStyles.contextualMenu?.contextualMenu, stylingProviderThemeProperties, moreInfoUrl, informationProtectionStringTable?.sensitivityButtonLearnMoreLinkText);
            setSensitivityContextMenu(contextMenu);
        }
    }, [
        appliedSensitivityLabel,
        sensitivityLabels,
        onApplyLabel,
        sensitivityMenuItemAs,
        resolvedStyles.contextualMenu?.contextualMenu,
        stylingProviderThemeProperties,
        moreInfoUrl,
        informationProtectionStringTable?.sensitivityButtonLearnMoreLinkText
    ]);
    const labelDisplay = React.useMemo(()=>{
        return (parentLabel ? parentLabel.name + '/' : '') + (currentLabel ? currentLabel.name : '');
    }, [
        currentLabel,
        parentLabel
    ]);
    const labelIconType = React.useMemo(()=>{
        if (!currentLabel) {
            return SensitivityIconType.TagUnknown;
        }
        if (currentLabel.hasProtection) {
            return SensitivityIconType.TagProtected;
        }
        return SensitivityIconType.Tag;
    }, [
        currentLabel
    ]);
    const hasNoAppliedLabel = React.useMemo(()=>{
        return !labelDisplay && !currentLabel;
    }, [
        currentLabel,
        labelDisplay
    ]);
    const tooltipProps = {
        calloutProps: {
            isBeakVisible: false,
            // Ensure tooltip is not overlapped by callout container
            beakWidth: 0,
            styles: {
                root: {
                    borderRadius: '4px'
                }
            }
        },
        onRenderContent: ()=>/*#__PURE__*/ React.createElement("div", {
                "data-testid": sensitivityLabelTooltipId,
                className: mergeStyles(resolvedStyles.tooltip?.tooltipWrapper)
            }, currentLabel && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
                className: mergeStyles(resolvedStyles.tooltip?.tooltipTitle)
            }, labelDisplay), /*#__PURE__*/ React.createElement("div", {
                className: mergeStyles(resolvedStyles.tooltip?.tooltipBodyText)
            }, currentLabel.tooltip)), hasNoAppliedLabel && /*#__PURE__*/ React.createElement("div", {
                className: mergeStyles(resolvedStyles.tooltip?.tooltipBodyText)
            }, informationProtectionStringTable?.noLabelTooltip))
    };
    const sensitivityButtonAriaLabel = React.useMemo(()=>{
        return `${informationProtectionStringTable?.sensitivityButtonAriaLabel}: ${labelDisplay} - ${hasNoAppliedLabel ? informationProtectionStringTable?.noLabelTooltip : currentLabel?.tooltip}`;
    }, [
        informationProtectionStringTable,
        labelDisplay,
        currentLabel,
        hasNoAppliedLabel
    ]);
    if (!supportsCLP || !isGetLabelEnabled(settingsProvider) || isGetLabelEnabled(settingsProvider) && !isSetLabelEnabled(settingsProvider) && hasNoAppliedLabel) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        ref: onRefChange,
        className: mergeStyles(resolvedStyles.wrapper, stylingProviderThemeProperties)
    }, !hideDivider && /*#__PURE__*/ React.createElement("div", {
        "data-testid": sensitivityButtonDividerId,
        className: mergeStyles(resolvedStyles.divider)
    }), /*#__PURE__*/ React.createElement(TooltipHost, {
        tooltipProps: tooltipProps,
        directionalHint: DirectionalHint.bottomCenter,
        calloutProps: {
            className: mergeStyles(stylingProviderThemeProperties),
            styles: resolvedStyles.tooltip?.tooltipCallout
        },
        hidden: showContextualMenu || isMobileDevice
    }, /*#__PURE__*/ React.createElement(DefaultButton, {
        elementRef: buttonRef,
        "data-testid": sensitivityButtonId,
        styles: resolvedStyles.button,
        onClick: toggleContextualMenu,
        "aria-label": sensitivityButtonAriaLabel,
        disabled: !canWrite
    }, showSpinner ? /*#__PURE__*/ React.createElement(Spinner, {
        size: SpinnerSize.xSmall,
        styles: sensitivitySpinnerStyle
    }) : /*#__PURE__*/ React.createElement(SensitivityIcon, {
        type: labelIconType
    }), !isCompact && labelDisplay ? /*#__PURE__*/ React.createElement("span", {
        "data-testid": sensitivityButtonLabelId,
        className: mergeStyles(resolvedStyles.buttonText)
    }, labelDisplay) : undefined), /*#__PURE__*/ React.createElement(ContextualMenu, {
        items: sensitivityContextMenu,
        hidden: !showContextualMenu,
        target: buttonRef,
        onDismiss: dismissContextualMenu,
        contextualMenuItemAs: sensitivityMenuItemAs,
        styles: resolvedStyles.contextualMenu?.contextualMenu,
        calloutProps: {
            className: mergeStyles(stylingProviderThemeProperties)
        },
        isBeakVisible: false
    })));
}, getSensitivityButtonDefaultStyle));
function readThemingProperties(themeProperties) {
    let cssProperties;
    themeProperties?.forEach((themeValue, themeKey)=>{
        if (cssProperties === undefined) {
            cssProperties = {};
        }
        cssProperties[themeKey] = themeValue;
    });
    return cssProperties;
}
